/**
 * Строка блока о его рядах (#2394 §9 п. 7, спека
 * docs/plans/2026-10-19-voc-trends-ui.md §1): что значит линия у строки, и
 * почему её нет или она с пропусками. Пустой ряд без объяснения недопустим
 * (решение владельца 14.10): на проде до заполнения истории корзины пусты, и
 * блок говорит, что динамика ещё собирается, а числа строк уже точные —
 * если блок не считал их сканом, упёршимся в потолок (#2596).
 */
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { VoiceSeriesBlock } from './useVoiceSeries';

interface VoiceSeriesNoteProps {
  block: VoiceSeriesBlock;
  /** Своё N у ряда — сказать словами, чьё (возражения: все проанализированные). */
  baseNote?: string;
  /**
   * Числа строк блока посчитаны сканом, упёршимся в потолок, — нижняя
   * граница (#2596): строка «динамики нет» не обещает их точности.
   */
  lowerBound?: boolean;
  /**
   * Только строки этих блоков посчитаны таким сканом, остальные точные
   * («Что изменилось» со строками нескольких блоков): строка называет их.
   */
  lowerBoundBlocks?: readonly string[];
}

export function VoiceSeriesNote({
  block,
  baseNote,
  lowerBound = false,
  lowerBoundBlocks = [],
}: VoiceSeriesNoteProps) {
  const { t } = useTranslation('dashboard');
  if (block.status === 'off' || block.status === 'pending') return null;
  // Refused for good: why, once, and no retry that can only fail again. A
  // failure — with a retry. The other signals of the block that drew still
  // get their lines below (`coverage` is theirs).
  const head =
    block.status === 'unavailable' ? (
      <p>{t(`voicePage.series.unavailable.${block.refusal ?? 'other'}`)}</p>
    ) : block.status === 'failed' ? (
      <div className="flex flex-wrap items-center gap-2">
        <span>{t('voicePage.series.failed')}</span>
        <Button variant="ghost" size="sm" onClick={block.retry}>
          {t('voicePage.series.retry')}
        </Button>
      </div>
    ) : null;
  const lines =
    block.coverage === null
      ? []
      : block.coverage === 'none'
        ? [
            lowerBoundBlocks.length > 0
              ? t('voicePage.series.collectingTruncatedSome', {
                  blocks: lowerBoundBlocks.join(', '),
                })
              : t(
                  lowerBound
                    ? 'voicePage.series.collectingTruncated'
                    : 'voicePage.series.collecting'
                ),
          ]
        : [
            t(`voicePage.series.hint.${block.unit}`),
            ...(baseNote ? [baseNote] : []),
            ...(block.coverage === 'partial' ? [t('voicePage.series.partial')] : []),
            ...(block.themeGaps ? [t('voicePage.series.themeGaps')] : []),
            ...(block.breaks ? [t('voicePage.series.breaks')] : []),
            ...(block.themeBreaks ? [t('voicePage.series.themeBreaks')] : []),
            // Each stale signal in its own words: a re-merged glossary is not a
            // new label map.
            ...block.stale.flatMap((signal) =>
              signal === 'competitor' || signal === 'objection'
                ? [t(`voicePage.series.stale.${signal}`)]
                : []
            ),
          ];
  return (
    <div className="mb-3 space-y-1 text-xs text-fg-muted" data-testid="voice-series-note">
      {head}
      {lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
}
